import React from "react";
import { useTranslation } from "react-i18next";
import { StyledH2, StyledLabel, StyledTextInput } from "@aureskonnect/react-ui";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { AvForm } from "availity-reactstrap-validation";
import moment from "moment";
import classnames from "classnames";

import { store as userStore } from "@components/VerticalLayout/store";
import { store, setIsCreatedCoping } from "../store";

import { capitalize } from "@helpers/general";
import CardFooterCreatedCoping from "./CardFooterCreatedCoping";
import {
    buildExistingDesignations,
    isDuplicateDesignation as computeIsDuplicate,
} from "./designationCheck";

import "../index.css";

const DESIGNATION_ERROR_ID = "printer-duplicate-designation-error";

type ModalCreatedCopingType = {
    rowData: any;
    setIsDataUpdated: React.Dispatch<React.SetStateAction<boolean>>;
    shopId: string;
    dataProject: any;
    setSelectedRows: Function;
};

export default function ModalCreatedCoping({
    rowData,
    setIsDataUpdated,
    shopId,
    dataProject,
    setSelectedRows,
}: ModalCreatedCopingType) {
    const { t } = useTranslation();
    const { isCreatedCoping, dataTable } = useSnapshot(store);
    const { oneShop } = useSnapshot(userStore);

    const printerNameKey = t("Printer name");

    const existingDesignations = React.useMemo<Set<string>>(
        () =>
            buildExistingDesignations(
                dataTable as any,
                oneShop,
                rowData.shopId,
                printerNameKey
            ),
        [dataTable, oneShop, rowData.shopId, printerNameKey]
    );

    const [designation, setDesignation] = React.useState(
        `${rowData[printerNameKey]}_${t("Copy")}_${moment().format(
            "DD/MM/YY - HH:mm:ss"
        )}`
    );
    const [inputDesignation, setInputDesignation] = React.useState(false);

    // Derived: recompute on every designation/existingDesignations change so
    // the inline error stays in sync without relying on onChange firing
    // first (initial render, programmatic value updates, or late-loading
    // dataTable all behave correctly).
    const isDuplicateDesignation = React.useMemo(
        () => computeIsDuplicate(designation, existingDesignations),
        [designation, existingDesignations]
    );

    const handleDesignationChange = (e: any) => {
        const value: string = e?.target?.value ?? "";
        setDesignation(value);
        setInputDesignation(value.trim() === "");
    };

    const hasError = inputDesignation || isDuplicateDesignation;

    return (
        <React.Fragment>
            <Modal centered isOpen={isCreatedCoping}>
                <ModalHeader
                    toggle={() => {
                        setIsCreatedCoping!(!isCreatedCoping);
                        setSelectedRows([]);
                    }}
                >
                    <StyledH2>{capitalize(t("Printer duplication"))}</StyledH2>
                </ModalHeader>
                <ModalBody
                    style={{ font: "normal normal 400 18px/30px Segoe UI" }}
                >
                    {t("Please enter the name of the new printer to create")}.
                    <AvForm>
                        <StyledLabel className="mt-3" htmlFor="example-input">
                            {t("Printer designation")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            id="remark"
                            name="remark"
                            onChange={handleDesignationChange}
                            value={designation}
                            data-testid="printer-duplicate-designation-input"
                            aria-invalid={hasError ? "true" : "false"}
                            aria-describedby={
                                hasError ? DESIGNATION_ERROR_ID : undefined
                            }
                            className={classnames({
                                input__clz: hasError,
                            })}
                        />
                        {hasError ? (
                            <div
                                id={DESIGNATION_ERROR_ID}
                                role="alert"
                                aria-live="polite"
                                className="printer-duplicate-error__clz"
                                data-testid={
                                    inputDesignation
                                        ? "printer-duplicate-empty-error"
                                        : "printer-duplicate-duplicate-error"
                                }
                            >
                                {inputDesignation
                                    ? t("Please enter a designation")
                                    : t("The name already exists")}
                            </div>
                        ) : null}
                    </AvForm>
                </ModalBody>
                <CardFooterCreatedCoping
                    rowData={rowData}
                    setIsDataUpdated={setIsDataUpdated}
                    shopId={shopId}
                    dataProject={dataProject}
                    setSelectedRows={setSelectedRows}
                    designation={designation}
                    setInputDesignation={setInputDesignation}
                    isDuplicateDesignation={isDuplicateDesignation}
                />
            </Modal>
        </React.Fragment>
    );
}
